import React, { useEffect, useRef, useState } from "react";
import { theme } from "antd";
import type { GlobalToken } from "antd";
import { useTranslate } from "@refinedev/core";
import { SparkleIcon, WarningCircleIcon } from "@phosphor-icons/react";
import styled from "styled-components";
import { Button, radius } from "@libertify/ui";
// Quota wiring kept for when credits/quota is available (see commented block below).
// import { useQuota } from "hooks/useQuota";
// import { QuotaFeatures } from "@/constants";

// TODO: replace with a real per-experience credit estimate once the backend
// exposes one. Hardcoded for now and intentionally not displayed.
// const ESTIMATED_CREDITS = 800;

/** How long the blocked-reason card stays up after a blocked click. */
const FLASH_MS = 3000;

interface CreateNowBarProps {
  /** Why creation is blocked, ready to display. `undefined` = creatable.
   *  Names the blocked click for assistive tech, and is the text flashed
   *  above the bar once that click lands. */
  blockedReason?: string;
  isCreating: boolean;
  onCreate: () => void;
  /** Clicked while blocked — the parent routes the user to whatever fixes it.
   *  Return `false` when it already surfaced the reason some other way (a
   *  modal, say) and the flash would just be noise. */
  onBlockedClick: () => boolean | void;
}

/**
 * Fixed bottom-right "Create now" bar shared by the whole flow. Shows the
 * primary action. While blocked the button
 * keeps the real `disabled` attribute — so its grayed-out look comes for free
 * from the existing variant styles — and an invisible overlay sits on top of
 * it: a disabled button never fires click, so the overlay is what routes the
 * click to `onBlockedClick` and raises the reason card.
 */
export const CreateNowBar: React.FC<CreateNowBarProps> = ({
  blockedReason,
  isCreating,
  onCreate,
  onBlockedClick,
}) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const isBlocked = !!blockedReason;

  const [flashed, setFlashed] = useState<string | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(flashTimer.current), []);
  const handleBlockedClick = () => {
    if (onBlockedClick() === false) return;
    setFlashed(blockedReason ?? null);
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlashed(null), FLASH_MS);
  };
  // Gated on `isBlocked` too, so fixing the problem retires the card at once
  // instead of leaving a stale warning up for the rest of its 3s.
  const warning = isBlocked ? flashed : null;

  // Quota-based cost/gating — re-enable once we surface real credits:
  // const { isBlocked, remaining } = useQuota();
  // const quotaBlocked = isBlocked(QuotaFeatures.MediaProject);
  // const remainingCount = remaining(QuotaFeatures.MediaProject);
  // const canCreate = ready && !quotaBlocked;

  return (
    <FixedWrap>
      {warning && (
        <WarningToast $token={token} role="alert">
          <WarningCircleIcon
            size={22}
            weight="fill"
            color={token.colorWarning}
          />
          <span>{warning}</span>
        </WarningToast>
      )}
      {/* Credits pill — re-enable once we surface real credits: wrap
          ButtonSlot back in <Bar> (moving its background and shadow there):
      <Bar $token={token}>
        <Credits $token={token}>
          <b>~{ESTIMATED_CREDITS.toLocaleString()}</b>{" "}
          {t("createExperience.createBar.credits")}
        </Credits>
        <ButtonSlot>...</ButtonSlot>
      </Bar>
      */}
      <ButtonSlot $token={token}>
        <Button
          variant="primary"
          size="l"
          leftIcon={<SparkleIcon size={18} weight="fill" />}
          loading={isCreating}
          disabled={isBlocked || isCreating}
          onClick={onCreate}
        >
          {t("createExperience.createBar.create")}
        </Button>
        {isBlocked && !isCreating && (
          <ClickCatcher
            type="button"
            aria-label={blockedReason}
            onClick={handleBlockedClick}
          />
        )}
      </ButtonSlot>
    </FixedWrap>
  );
};

// ─── Styled components ───

interface TokenProps {
  $token: GlobalToken;
}

// Fixed bottom-right anchor for the bar and (when shown) the warning card
// stacked above it — keeping both in one flex column means the warning
// naturally sits right above the bar with no manual offset math.
const FixedWrap = styled.div`
  position: fixed;
  right: 28px;
  bottom: 28px;
  z-index: 60;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;

  @media (width <= 600px) {
    left: 20px;
    right: 20px;
  }
`;

const WarningToast = styled.div<TokenProps>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  max-width: 380px;
  padding: 16px 20px;
  border-radius: ${radius.box}px;
  background: ${({ $token }) => $token.colorBgContainer};
  border: 1.5px solid ${({ $token }) => $token.colorWarningBorder};
  box-shadow: ${({ $token }) => $token.boxShadow};
  font-size: ${({ $token }) => $token.fontSizeLG}px;
  font-weight: 600;
  line-height: 1.4;
  color: ${({ $token }) => $token.colorText};

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }

  @media (width <= 600px) {
    max-width: none;
  }
`;

// Wraps the button so the click-catcher overlay (below) can cover it exactly.
// The opaque background keeps the disabled button's translucent fill from
// showing the page through; the shadow lifts it off the content.
const ButtonSlot = styled.span<TokenProps>`
  position: relative;
  display: inline-flex;
  border-radius: ${radius.pill}px;
  background: ${({ $token }) => $token.colorBgElevated};
  box-shadow: ${({ $token }) => $token.boxShadow};
`;

// Credits pill styles — re-enable once we surface real credits:
// const Bar = styled.div<TokenProps>`
//   display: flex;
//   align-items: center;
//   gap: 18px;
//   padding: 10px 12px 10px 22px;
//   border-radius: 999px;
//   background: ${({ $token }) => $token.colorBgElevated};
//   border: 1px solid ${({ $token }) => $token.colorBorderSecondary};
//   box-shadow: ${({ $token }) => $token.boxShadow};
//
//   @media (width <= 600px) {
//     width: 100%;
//     justify-content: space-between;
//   }
// `;
//
// const Credits = styled.span<TokenProps>`
//   font-size: ${({ $token }) => $token.fontSize}px;
//   color: ${({ $token }) => $token.colorTextSecondary};
//
//   b {
//     color: ${({ $token }) => $token.colorPrimary};
//   }
// `;

// Transparent overlay sitting on top of the real (disabled) button, so a
// click while blocked still reaches the parent — a native disabled button
// never fires click at all.
const ClickCatcher = styled.button`
  position: absolute;
  inset: 0;
  border: none;
  background: transparent;
  cursor: pointer;
`;
